/**
 * **O que um valor de configuração tem de ser antes de valer como implantação.**
 *
 * Três perguntas, e uma só resposta para toda a gente: isto ainda é o exemplo
 * por preencher? isto aponta para esta máquina? isto é um endereço que um
 * navegador pode abrir em público?
 *
 * ## Porque aqui e não em cada processo
 *
 * Porque já esteve em cada processo. O host de batalha tinha a sua expressão,
 * a API não tinha nenhuma e o cliente tinha um recuo para `127.0.0.1` — três
 * respostas diferentes à mesma pergunta, e a mais permissiva ganhava sempre,
 * porque bastava ser a que estava no caminho do arranque.
 *
 * Um módulo sem dependências, sem ambiente e sem I/O: recebe cordas, devolve
 * juízos. Quem decide o que fazer com o juízo é quem chama — a API recusa o
 * pedido, o host recusa o arranque, a compilação do cliente recusa produzir
 * `dist`.
 *
 * ## O que este ficheiro NUNCA faz
 *
 * Ler `process.env`, `import.meta.env` ou um ficheiro; imprimir um valor;
 * corrigir um valor. Um valor mau é recusado com o **nome** da variável e
 * nunca com o que ela continha: uma recusa não é sítio para publicar
 * configuração.
 */

/**
 * **A superfície de plataforma que isto usa, declarada.**
 *
 * `packages/shared` compila com `"types": []` e `lib: ES2023` de propósito: é
 * o que garante que nada aqui depende do navegador nem do Node. `URL` existe
 * nos dois, mas não está em `lib`, e alargar a biblioteca para a apanhar
 * abriria a porta a `document`, `process` e ao resto — que é exactamente o que
 * a regra 3 proíbe.
 */
interface UrlLike {
  readonly protocol: string;
  readonly hostname: string;
  readonly username: string;
  readonly password: string;
}

const platform = globalThis as unknown as {
  readonly URL: new (input: string) => UrlLike;
};

/**
 * **Um placeholder não é um valor.**
 *
 * `.env.example` existe para ser copiado, e uma cópia que ninguém preencheu é
 * a falha que isto apanha: `<o-dominio-do-jogo>` passa por um nome de anfitrião
 * em quase todo o lado, sobrevive a todas as outras verificações, e produz um
 * serviço que parece configurado e não serve ninguém.
 *
 * Construído a partir de cordas e não escrito como literal porque um literal
 * destes já foi enviado com caracteres de *backspace* verdadeiros onde queria
 * fronteiras de palavra — e durante um tempo não correspondeu a nada.
 */
const PLACEHOLDER = new RegExp(
  [
    /* Os sinais do exemplo. `new URL` também os recusa, e são a metade fácil. */
    '[<>]',
    /*
     * E os que um leitor de URL aceita de bom grado, que são a metade perigosa:
     * um serviço apontado com confiança a um domínio que ninguém tem parece
     * configurado, e o primeiro a reparar é um jogador.
     */
    'change[-_]?me',
    'replace[-_]?me',
    'your[-_]?(domain|host|api|token|secret)',
    'example[-_.]?(com|org|net)',
    'todo',
    'xxxx+',
  ].join('|'),
  'i',
);

/** `true` quando o valor ainda é o do exemplo. */
export function isConfigPlaceholder(value: string): boolean {
  return PLACEHOLDER.test(value);
}

/**
 * **Esta máquina não é um destino público.**
 *
 * Um `localhost` em produção não falha: aceita ligações da própria máquina,
 * responde bem a quem lá estiver, e parece estar a funcionar enquanto ninguém
 * de fora consegue jogar. É por isso que é uma recusa e não um aviso.
 *
 * `.local` entra porque o mDNS tem exactamente o mesmo efeito, e um endereço
 * privado (`10.`, `192.168.`, `172.16-31.`) porque uma rede interna não é a
 * Internet.
 */
export function isLocalHostname(hostname: string): boolean {
  const host = hostname
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '');
  if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local')) {
    return true;
  }
  if (host === '::1' || host === '0.0.0.0' || host === '::') {
    return true;
  }
  if (/^127\./.test(host) || /^10\./.test(host) || /^192\.168\./.test(host)) {
    return true;
  }
  return /^172\.(1[6-9]|2[0-9]|3[01])\./.test(host);
}

/** Os ambientes onde nada disto é negociável. */
export type DeployEnvironment = 'development' | 'test' | 'staging' | 'production';

/**
 * `staging` conta como produção. Um ensaio que só funciona porque aponta para
 * a máquina de quem o fez não é prova de que produção funcionaria.
 */
export function isDeployedEnvironment(environment: string): boolean {
  return environment === 'staging' || environment === 'production';
}

/** O que está errado com um endereço, ou `null` quando não está nada. */
export type DeployUrlProblem =
  | 'not_a_url'
  | 'placeholder'
  | 'local'
  | 'insecure'
  | 'has_credentials';

/**
 * **Um endereço que um navegador tem de conseguir abrir, de fora.**
 *
 * Aplicada apenas em `staging` e `production`: em desenvolvimento o endereço é
 * `http://127.0.0.1` de propósito, e recusá-lo seria recusar o `pnpm dev`.
 *
 * `secure` diz se o esquema tem de ser o cifrado. É um parâmetro e não uma
 * dedução porque `https` e `wss` são o mesmo requisito escrito de duas maneiras,
 * e quem chama sabe qual dos dois está a validar.
 */
export function deployUrlProblem(
  value: string,
  options: { readonly secure: boolean },
): DeployUrlProblem | null {
  if (isConfigPlaceholder(value)) {
    return 'placeholder';
  }
  let url: UrlLike;
  try {
    url = new platform.URL(value);
  } catch {
    return 'not_a_url';
  }
  if (url.username.length > 0 || url.password.length > 0) {
    /* Uma credencial numa URL vai parar a um registo, a um `Referer` e a um histórico. */
    return 'has_credentials';
  }
  if (isLocalHostname(url.hostname)) {
    return 'local';
  }
  if (options.secure && url.protocol !== 'https:' && url.protocol !== 'wss:') {
    return 'insecure';
  }
  return null;
}

/** A frase que uma recusa usa. Nomeia a variável e o defeito, nunca o valor. */
export function describeDeployUrlProblem(name: string, problem: DeployUrlProblem): string {
  switch (problem) {
    case 'not_a_url':
      return `${name} is not a URL`;
    case 'placeholder':
      return `${name} still holds a placeholder`;
    case 'local':
      return `${name} points at this machine, which is not a public destination`;
    case 'insecure':
      return `${name} must be https (or wss)`;
    case 'has_credentials':
      return `${name} may not carry credentials in the URL`;
  }
}
